import React, { useState, useEffect } from 'react';
import { Event, Document, DocumentViewResponse, DocumentMetadata, DocumentTag, DocumentQuote, DocumentDate } from '../types';
import { 
  FileText, 
  Download, 
  Eye, 
  File, 
  Image, 
  FileVideo, 
  FileAudio,
  AlertCircle,
  Loader,
  Tag,
  Calendar,
  Quote,
  Info
} from 'lucide-react';

interface DocumentViewerProps {
  selectedEvent: Event | null;
  documents: Document[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

const DocumentViewer: React.FC<DocumentViewerProps> = ({ selectedEvent, documents }) => {
  const [viewingDocument, setViewingDocument] = useState<Document | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<DocumentMetadata[]>([]);
  const [tags, setTags] = useState<DocumentTag[]>([]);
  const [quotes, setQuotes] = useState<DocumentQuote[]>([]);
  const [dates, setDates] = useState<DocumentDate[]>([]);
  const [activeTab, setActiveTab] = useState<'preview' | 'metadata' | 'quotes'>('preview');

  const getFileIcon = (fileType: string | null) => {
    if (!fileType) return File;
    
    if (fileType.startsWith('image/')) return Image;
    if (fileType.startsWith('video/')) return FileVideo;
    if (fileType.startsWith('audio/')) return FileAudio;
    if (fileType.includes('pdf')) return FileText;
    
    return File;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDocumentClick = async (document: Document) => {
    setLoading(true);
    setError(null);
    setViewingDocument(document);

    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/view/${document.id}`);
      
      if (!response.ok) {
        throw new Error('Failed to get document URL');
      }

      const data: DocumentViewResponse = await response.json();
      setDocumentUrl(data.url);

      // Load metadata for the document
      await loadDocumentMetadata(document.event_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document');
      console.error('Error loading document:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentMetadata = async (eventId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}/metadata`);
      if (response.ok) {
        const data = await response.json();
        setMetadata(data.metadata || []);
        setTags(data.tags || []);
        setQuotes(data.quotes || []);
        setDates(data.dates || []);
      }
    } catch (err) {
      console.error('Error loading metadata:', err);
    }
  };

  const handleDownload = async (document: Document) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/documents/view/${document.id}`);
      
      if (!response.ok) {
        throw new Error('Failed to get document URL');
      }

      const data: DocumentViewResponse = await response.json();
      
      // Create a temporary link to download the file
      const link = window.document.createElement('a');
      link.href = data.url;
      link.download = document.file_name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const renderDocumentContent = () => {
    if (!viewingDocument || !documentUrl) return null;

    const fileType = viewingDocument.file_type;
    
    if (fileType?.includes('pdf')) {
      return (
        <iframe
          src={documentUrl}
          className="w-full h-full border-0 rounded"
          title={viewingDocument.file_name}
        />
      );
    }
    
    if (fileType?.startsWith('image/')) {
      return (
        <div className="flex items-center justify-center h-full">
          <img
            src={documentUrl}
            alt={viewingDocument.file_name}
            className="max-w-full max-h-full object-contain rounded"
          />
        </div>
      );
    }
    
    if (fileType?.startsWith('text/') || fileType?.includes('html')) {
      return (
        <iframe
          src={documentUrl}
          className="w-full h-full border-0 rounded"
          title={viewingDocument.file_name}
        />
      );
    }
    
    // For unsupported file types, show a message
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FileText className="h-16 w-16 mb-4 text-gray-300" />
        <p className="text-lg font-medium mb-2">Preview not available</p>
        <p className="text-sm text-center mb-4">
          This file type cannot be previewed in the browser.
        </p>
        <button
          onClick={() => handleDownload(viewingDocument)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download className="h-4 w-4 mr-2" />
          Download to view
        </button>
      </div>
    );
  };

  if (!selectedEvent) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-500">
        <FileText className="h-16 w-16 mb-4 text-gray-300" />
        <p className="text-lg font-medium mb-2">No Event Selected</p>
        <p className="text-sm text-center">
          Select an event from the timeline to view associated documents.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Event Info Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900 mb-1">{selectedEvent.title}</h3>
        <p className="text-sm text-gray-600">
          {new Date(selectedEvent.event_date).toLocaleDateString()} • {documents.length} document{documents.length !== 1 ? 's' : ''}
        </p>
      </div>

      {documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-gray-500">
          <FileText className="h-12 w-12 mb-4 text-gray-300" />
          <p className="text-lg font-medium mb-2">No Documents</p>
          <p className="text-sm text-center">
            No documents are associated with this event.
          </p>
        </div>
      ) : (
        <div className="flex h-full">
          {/* Document List */}
          <div className="w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-3 border-b border-gray-200 bg-gray-50">
              <h4 className="font-medium text-sm text-gray-700">Documents</h4>
            </div>
            <div className="flex-1 overflow-y-auto">
              {documents.map((document) => {
                const Icon = getFileIcon(document.file_type);
                const isSelected = viewingDocument?.id === document.id;
                
                return (
                  <div
                    key={document.id}
                    className={`p-3 border-b border-gray-100 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleDocumentClick(document)}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {document.file_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {document.file_type} • {formatFileSize(document.file_size)}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(document.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(document);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Document Preview */}
          <div className="flex-1 flex flex-col">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <Loader className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
                  <p className="text-gray-600">Loading document...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
                  <p className="text-lg font-medium text-gray-900 mb-2">Error Loading Document</p>
                  <p className="text-sm text-gray-600 mb-4">{error}</p>
                  <button
                    onClick={() => viewingDocument && handleDocumentClick(viewingDocument)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : viewingDocument ? (
              <div className="flex-1 flex flex-col">
                {/* Document Header */}
                <div className="p-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Eye className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {viewingDocument.file_name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleDownload(viewingDocument)}
                      className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab('preview')}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === 'preview'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Preview
                  </button>
                  <button
                    onClick={() => setActiveTab('metadata')}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === 'metadata'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Metadata ({metadata.length + tags.length + dates.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('quotes')}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === 'quotes'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Quotes ({quotes.length})
                  </button>
                </div>
                
                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto">
                  {activeTab === 'preview' && (
                    <div className="h-full document-viewer">
                      {renderDocumentContent()}
                    </div>
                  )}
                  
                  {activeTab === 'metadata' && (
                    <div className="p-4 space-y-4">
                      {/* Tags */}
                      {tags.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <Tag className="h-4 w-4 mr-1" />
                            Tags
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                title={tag.tag_description}
                              >
                                {tag.tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Dates */}
                      {dates.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            Dates
                          </h4>
                          <div className="space-y-1">
                            {dates.map((date) => (
                              <div key={date.id} className="text-sm text-gray-600">
                                <span className="font-medium">{date.raw_date_value}</span>
                                {date.date_iso_string && (
                                  <span className="ml-2 text-gray-500">
                                    ({new Date(date.date_iso_string).toLocaleDateString()})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Metadata */}
                      {metadata.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                            <Info className="h-4 w-4 mr-1" />
                            Metadata
                          </h4>
                          <div className="space-y-1">
                            {metadata.map((meta) => (
                              <div key={meta.id} className="text-sm">
                                <span className="font-medium text-gray-700">{meta.metadata_key}:</span>
                                <span className="ml-2 text-gray-600">{meta.metadata_value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {tags.length === 0 && dates.length === 0 && metadata.length === 0 && (
                        <div className="text-center text-gray-500 py-8">
                          <Info className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p>No metadata available for this document</p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'quotes' && (
                    <div className="p-4 space-y-4">
                      {quotes.length > 0 ? (
                        quotes.map((quote) => (
                          <div key={quote.id} className="border-l-4 border-blue-500 pl-4 py-2">
                            <div className="text-sm text-gray-600 mb-2">
                              Page {quote.page_number}
                            </div>
                            <blockquote className="text-gray-800 italic mb-2">
                              "{quote.quote}"
                            </blockquote>
                            {quote.rationale && (
                              <div className="text-xs text-gray-500">
                                <strong>Rationale:</strong> {quote.rationale}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500 py-8">
                          <Quote className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                          <p>No quotes available for this document</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  <Eye className="h-12 w-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">Select a Document</p>
                  <p className="text-sm">
                    Choose a document from the list to preview it here.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;
